import { Helmet } from 'react-helmet-async'
import { SITE, absoluteUrl, titleFor } from './site'

type SeoProps = {
  title?: string
  description?: string
  path?: string
  keywords?: string[]
  image?: string
  type?: 'website' | 'article' | 'music.radio_station'
  noindex?: boolean
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

export function Seo({
  title,
  description = SITE.defaultDescription,
  path = '/',
  keywords = [...SITE.defaultKeywords],
  image = SITE.ogImage,
  type = 'website',
  noindex = false,
  jsonLd,
}: SeoProps) {
  const fullTitle = titleFor(title)
  const url = absoluteUrl(path)
  const keywordString = [...new Set(keywords)].join(', ')
  const schemas = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : []
  const imageAbs = image.startsWith('http') ? image : absoluteUrl(image)
  const isDefaultOg = imageAbs === SITE.ogImage

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywordString} />
      <link rel="canonical" href={url} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}
      <meta name="googlebot" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="author" content="Music24" />
      <meta name="theme-color" content="#070707" />

      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={SITE.locale} />
      <meta property="og:type" content={type === 'music.radio_station' ? 'website' : type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageAbs} />
      <meta property="og:image:alt" content={fullTitle} />
      {isDefaultOg && (
        <>
          <meta property="og:image:width" content={String(SITE.ogImageWidth)} />
          <meta property="og:image:height" content={String(SITE.ogImageHeight)} />
        </>
      )}
      <meta property="og:image:type" content="image/png" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageAbs} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  )
}
